import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types
import { EffectiveProfile, MessageGenerator, Rank, RestrictionValidator, WithKey } from "../types";

// State
import { AppState } from "./store";

// Components
import { FormFieldHelpCardProps } from "../components/FormFieldHelpCard";

import parse from "parse-duration";

export namespace SignKeyForm {
  export interface Restriction {
    message: MessageGenerator;
    display: RestrictionValidator
  }

  interface Field<T> {
    value: T;
    examples?: WithKey<FormFieldHelpCardProps>[];
    examplesTitle?: string;
    restrictions?: WithKey<Restriction>[];
  }

  export interface Values {
    username: string;
    publicKey: string;
    ttl: string;
    sourceAddress: string;
  }

  export type State = {
    [Property in keyof Values]: Field<Values[Property]>;
  };
}

export const labels: {
  readonly [Property in keyof SignKeyForm.Values]: string;
} = {
  publicKey: "Public Key",
  sourceAddress: "Source Address",
  ttl: "TTL",
  username: "Username",
};

export const initialState: SignKeyForm.State = {
  username: {
    value: "",
    restrictions: [
      {
        key: "onlyAdminCanSpecifyUsername",
        display: (rank) => rank >= Rank.Admin,
        message: () =>
          "According to the policy assigned to your account you cannot change" +
          " a username for signing request",
      },
    ],
    examples: [
      {
        key: "johnDoe",
        title: "John Doe",
        subtitle: "john.doe@bt.com",
        children: "john.doe",
      },
      {
        key: "edwardKowalski",
        title: "Edward Kowalski",
        subtitle: "edward.kowalski@bt.com",
        children: "edward.kowalski",
      },
    ],
  },
  publicKey: {
    value: "",
    examples: [
      {
        key: "rsa",
        title: "RSA",
        children: "ssh-rsa AAAAB3NzaC1yc2EAAAADA...",
        link: {
          title: "Wikipedia: RSA",
          href: "https://en.wikipedia.org/wiki/RSA_(cryptosystem)",
        },
      },
      {
        key: "ed25519",
        title: "Ed25519",
        children: "ssh-ed25519 AAAAC3NzaC1lZDI1N...",
        link: {
          title: "Wikipedia: EdDSA / Ed25519",
          href: "https://en.wikipedia.org/wiki/EdDSA#Ed25519",
        },
      },
    ],
    examplesTitle: "Valid Key formats",
  },
  sourceAddress: {
    value: "",
    examples: [
      {
        key: "IPv4",
        title: "IPv4",
        children: "10.0.0.1",
        link: {
          title: "Wikipedia: IPv4 / Addressing",
          href: "https://en.wikipedia.org/wiki/IPv4#Addressing",
        },
      },
      {
        key: "IPv4CIDR",
        title: "IPv4 / CIDR notation",
        children: "10.0.0.1/32",
        link: {
          title: "Wikipedia: IPv4 / Addressing / Address Representation",
          href: "https://en.wikipedia.org/wiki/IPv4#Address_representations",
        },
      },
    ],
    restrictions: [
      {
        key: "privateSourceAddress",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.privateConfiguration() !== null,
        message: (effectiveProfile: EffectiveProfile) =>
          `According to the policy assigned to your account you cannot send a` +
          ` signing request for more than ${effectiveProfile.privateConfiguration().maxIpAddresses} private source address`,
      },
      {
        key: "noPrivateSourceAddress",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.privateConfiguration() === null,
        message: () =>
          `According to the policy assigned to your account you cannot send signing request for private source addresses`,
      },
      {
        key: "noPublicSourceAddress",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.publicConfiguration() === null,
        message: () =>
          `According to the policy assigned to your account you cannot send signing request for public source addresses`,
      },
      {
        key: "publicSourceAddress",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.publicConfiguration() !== null,
        message: () =>
          "According to the policy assigned to your account you cannot send a signing request for more than 1 public source address",
      },
      {
        key: "no0",
        display: () => true,
        message: () => "Addresses 0.0.0.0 is not allowed",
      },
      {
        key: "noMulticast",
        display: () => true,
        message: () => "Multicast addresses are not allowed",
      },
      {
        key: "noLoopback",
        display: () => true,
        message: () => "Loopback interface addresses are not allowed (127.0.0.0/8)",
      },
    ],
  },
  ttl: {
    value: "1d",
    examples: [
      {
        key: "1 day",
        children: "1 day",
      },
      {
        key: "1day",
        children: "1day",
      },
      {
        key: "1d",
        children: "1d",
      },
      {
        key: "24h",
        children: "24h",
      },
      {
        key: "1440 min",
        children: "1440 min",
      }
    ],
    restrictions: [
      {
        key: "maxPrivateTTL",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.maxPrivateIpAddresses() > 0 ,
        message: (effectiveProfile) => {
          const duration = parse(effectiveProfile.privateConfiguration().maxDuration.toString() + "s", "h");
          return `According to the policy assigned to your account you cannot send ` +
          `signing requests for private addresses with longer than ${duration} hours TTL`
        },
      },
      {
        key: "maxPublicTTL",
        display: (signedInUserRank, effectiveProfile) => effectiveProfile.maxPublicIpAddresses() > 0 ,
        message: (effectiveProfile) => {
          const duration = parse(effectiveProfile.publicConfiguration().maxDuration.toString() + "s", "h");
          return `According to the policy assigned to your account you cannot send ` +
            `signing requests for public addresses with longer than ${duration} hours TTL`
        },
      },
    ],
    examplesTitle: "Examples (all equivalent)",
  },
};

export const signKeyFormSlice = createSlice({
  name: "signKeyForm",
  initialState,
  reducers: {
    setUsername: (state, { payload }: PayloadAction<string>) => {
      state.username.value = payload;
    },
  },
});

export const { setUsername } = signKeyFormSlice.actions;

export const selectSignKeyForm = (state: AppState): SignKeyForm.State =>
  state.signKeyForm;

export const selectSignKeyFormField =
  (id: keyof SignKeyForm.State) => (state: AppState) =>
    state.signKeyForm[id];

export default signKeyFormSlice.reducer;
